/**
 * Zod-Schemas fuer die Login-Seite (Fachperson + Patient).
 *
 * Phase 3 der Forms-Migration auf react-hook-form + zod
 * (siehe docs/runbooks/FORMS_MIGRATION_PLAN.md).
 *
 * Statt einer discriminatedUnion ueber den Modus (signin/signup) und die
 * Rolle (fachperson/patient) verwenden wir bewusst kleinere Einzelschemas
 * — jeder useForm-Aufruf bekommt damit das passend zugeschnittene
 * Schema. Das vermeidet den optional/required-Spagat bei
 * Registrierungsfeldern, die im Login-Modus gar nicht existieren.
 *
 * Passwort-Regeln (Registrierung Fachperson, BSI-Empfehlung):
 *  - mind. 12 Zeichen, je 1x Gross-/Kleinbuchstabe, Zahl, Sonderzeichen
 *  - Live-Anzeige der Regeln bleibt UI-Verantwortung (PASSWORD_RULES in
 *    Login.tsx), das Schema validiert hart.
 */
import { z } from 'zod';

// BSI-konforme Passwort-Regel fuer Fachperson-Signup. Die UI-Hinweise in
// Login.tsx zeigen die einzelnen Kriterien live an; das Schema benutzt
// einen aggregierten Refine, damit beim Submit ein einzelner Fehler
// ausgegeben wird (UI zeigt die feine Aufschluesselung).
const STRONG_PASSWORD = z
  .string()
  .min(12, 'Passwort erfuellt nicht die Sicherheitsanforderungen.')
  .refine((pw) => /[A-Z]/.test(pw), {
    message: 'Passwort erfuellt nicht die Sicherheitsanforderungen.',
  })
  .refine((pw) => /[a-z]/.test(pw), {
    message: 'Passwort erfuellt nicht die Sicherheitsanforderungen.',
  })
  .refine((pw) => /[0-9]/.test(pw), {
    message: 'Passwort erfuellt nicht die Sicherheitsanforderungen.',
  })
  .refine((pw) => /[^A-Za-z0-9]/.test(pw), {
    message: 'Passwort erfuellt nicht die Sicherheitsanforderungen.',
  });

// ─── Fachperson ──────────────────────────────────────────────────────────────

export const fachpersonSigninSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'E-Mail ist erforderlich.')
    .email('Bitte eine gueltige E-Mail-Adresse eingeben.'),
  password: z.string().min(1, 'Passwort ist erforderlich.'),
});

export type FachpersonSigninInput = z.infer<typeof fachpersonSigninSchema>;

export const fachpersonSignupSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'E-Mail ist erforderlich.')
    .email('Bitte eine gueltige E-Mail-Adresse eingeben.'),
  password: STRONG_PASSWORD,
  firstName: z
    .string()
    .trim()
    .min(1, 'Vorname ist erforderlich.')
    .max(100, 'Vorname zu lang (max. 100 Zeichen).'),
  lastName: z
    .string()
    .trim()
    .min(1, 'Nachname ist erforderlich.')
    .max(100, 'Nachname zu lang (max. 100 Zeichen).'),
  orgName: z
    .string()
    .trim()
    .min(1, 'Name der Einrichtung ist erforderlich.')
    .max(200, 'Name zu lang (max. 200 Zeichen).'),
  agbAccepted: z.literal(true, {
    errorMap: () => ({
      message: 'Bitte akzeptieren Sie die AGB und Datenschutzerklaerung.',
    }),
  }),
});

export type FachpersonSignupInput = z.infer<typeof fachpersonSignupSchema>;

// ─── Patient ─────────────────────────────────────────────────────────────────

export const patientSigninSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'E-Mail ist erforderlich.')
    .email('Bitte eine gueltige E-Mail-Adresse eingeben.'),
  password: z.string().min(1, 'Passwort ist erforderlich.'),
});

export type PatientSigninInput = z.infer<typeof patientSigninSchema>;

export const patientSignupSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'E-Mail ist erforderlich.')
    .email('Bitte eine gueltige E-Mail-Adresse eingeben.'),
  password: z.string().min(8, 'Das Passwort muss mindestens 8 Zeichen lang sein.'),
  agbAccepted: z.literal(true, {
    errorMap: () => ({
      message: 'Bitte akzeptieren Sie die AGB und Datenschutzerklaerung.',
    }),
  }),
});

export type PatientSignupInput = z.infer<typeof patientSignupSchema>;
